import React, { useState, useEffect } from 'react';
import './Financeiro.css';
import { financeiroService } from '../services/financeiroService';

const Financeiro = ({ onNavigateToDashboard }) => {
  const [faturas, setFaturas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [resumo, setResumo] = useState(null);
  const [pagination, setPagination] = useState({ page: 1, limit: 50, total: 0, totalPages: 0 });
  const [periodo, setPeriodo] = useState(null);
  
  // Filtros
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth() + 1);
  const [ano, setAno] = useState(hoje.getFullYear());
  const [localidade, setLocalidade] = useState('todos');
  const [statusFilter, setStatusFilter] = useState('');

  useEffect(() => {
    if (mes && ano) {
      loadFaturas();
    }
  }, [mes, ano, localidade, statusFilter]);

  const loadFaturas = async (page = 1) => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        mes,
        ano,
        localidade,
        page,
        limit: 50,
        status: statusFilter || undefined
      };

      const response = await financeiroService.getFaturas(params);
      if (response.success) {
        setFaturas(response.data);
        setResumo(response.resumo);
        setPagination(response.pagination);
        setPeriodo(response.periodo);
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Erro ao carregar faturas');
    } finally {
      setLoading(false);
    }
  };

  const formatMoeda = (valor) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(valor || 0);
  };

  const formatData = (data) => {
    if (!data) return '-';
    return new Date(data).toLocaleDateString('pt-BR');
  };

  const getStatusClass = (status) => {
    switch(status) {
      case 'Paga': return 'status-paga';
      case 'Vencida': return 'status-vencida';
      case 'Pendente': return 'status-pendente';
      case 'Excluída': return 'status-excluida';
      default: return '';
    }
  };

  const meses = [
    { value: 1, label: 'Janeiro' },
    { value: 2, label: 'Fevereiro' },
    { value: 3, label: 'Março' },
    { value: 4, label: 'Abril' },
    { value: 5, label: 'Maio' },
    { value: 6, label: 'Junho' },
    { value: 7, label: 'Julho' },
    { value: 8, label: 'Agosto' },
    { value: 9, label: 'Setembro' },
    { value: 10, label: 'Outubro' },
    { value: 11, label: 'Novembro' },
    { value: 12, label: 'Dezembro' }
  ];

  const anos = [];
  for (let i = hoje.getFullYear(); i >= 2020; i--) {
    anos.push(i);
  }

  return (
    <div className="financeiro">
      <div className="financeiro-header">
        <h2>💰 Gestão Financeira</h2>
        <button className="btn-back" onClick={onNavigateToDashboard}>
          ← Voltar ao Dashboard
        </button>
      </div>

      {/* Filtros */}
      <div className="financeiro-filters">
        <div className="filter-row">
          <div className="filter-item">
            <label>📅 Mês</label>
            <select value={mes} onChange={(e) => setMes(parseInt(e.target.value))}>
              {meses.map(m => (
                <option key={m.value} value={m.value}>{m.label}</option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>📆 Ano</label>
            <select value={ano} onChange={(e) => setAno(parseInt(e.target.value))}>
              {anos.map(a => (
                <option key={a} value={a}>{a}</option>
              ))}
            </select>
          </div>

          <div className="filter-item">
            <label>📍 Localidade</label>
            <select value={localidade} onChange={(e) => setLocalidade(e.target.value)}>
              <option value="todos">Todas</option>
              <option value="vila-isabel">Vila Isabel</option>
              <option value="nilopolis">Nilópolis</option>
            </select>
          </div>

          <div className="filter-item">
            <label>🏷️ Status</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Todos</option>
              <option value="paga">✅ Pagas</option>
              <option value="vencida">❌ Vencidas (Inadimplentes)</option>
              <option value="pendente">⏳ Pendentes</option>
              <option value="excluida">🗑️ Excluídas</option>
            </select>
          </div>
        </div>
      </div>

      {loading && <div className="loading">⏳ Carregando dados financeiros...</div>}
      {error && <div className="error">❌ {error}</div>}

      {resumo && !loading && (
        <>
          {/* Resumo Financeiro */}
          <div className="resumo-financeiro">
            <h3>📊 Resumo de {periodo?.mesNome}/{periodo?.ano}</h3>
            
            <div className="resumo-grid">
              <div className="resumo-card card-total">
                <div className="resumo-icon">💵</div>
                <div className="resumo-content">
                  <span className="resumo-label">Valor Total</span>
                  <span className="resumo-value">{formatMoeda(resumo.valorTotal)}</span>
                  <span className="resumo-desc">{resumo.totalFaturas} faturas</span>
                </div>
              </div>

              <div className="resumo-card card-recebido">
                <div className="resumo-icon">✅</div>
                <div className="resumo-content">
                  <span className="resumo-label">Recebido</span>
                  <span className="resumo-value">{formatMoeda(resumo.valorRecebido)}</span>
                  <span className="resumo-desc">{resumo.faturasPagas} faturas pagas</span>
                  <span className="resumo-percent">{resumo.taxaRecebimento}%</span>
                </div>
              </div>

              <div className="resumo-card card-inadimplencia">
                <div className="resumo-icon">⚠️</div>
                <div className="resumo-content">
                  <span className="resumo-label">Inadimplência</span>
                  <span className="resumo-value">{formatMoeda(resumo.valorInadimplencia)}</span>
                  <span className="resumo-desc">{resumo.faturasVencidas} faturas vencidas</span>
                </div>
              </div>

              <div className="resumo-card card-pendente">
                <div className="resumo-icon">⏳</div>
                <div className="resumo-content">
                  <span className="resumo-label">A Receber</span>
                  <span className="resumo-value">{formatMoeda(resumo.valorPendente)}</span>
                  <span className="resumo-desc">{resumo.faturasPendentes} faturas pendentes</span>
                </div>
              </div>
            </div>
          </div>

          {/* Tabela de Faturas */}
          {faturas.length > 0 ? (
            <>
              <div className="table-container">
                <div className="table-wrapper">
                  <table>
                    <thead>
                      <tr>
                        <th>Código</th>
                        <th>Cliente</th>
                        <th>Localidade</th>
                        <th>Vencimento</th>
                        <th>Valor</th>
                        <th>Status</th>
                        <th>Dias Atraso</th>
                        <th>Pagamento</th>
                      </tr>
                    </thead>
                    <tbody>
                      {faturas.map((fatura) => (
                        <tr key={fatura.codfatura}>
                          <td className="td-codigo">{fatura.codfatura}</td>
                          <td className="td-nome">{fatura.nome_cliente}</td>
                          <td>
                            <span className="local-cell">{fatura.local}</span>
                          </td>
                          <td>{formatData(fatura.data_vencimento)}</td>
                          <td className="td-valor">{formatMoeda(fatura.valor_total)}</td>
                          <td>
                            <span className={`badge-status ${getStatusClass(fatura.status_pagamento)}`}>
                              {fatura.status_pagamento === 'Paga' && '✅ '}
                              {fatura.status_pagamento === 'Vencida' && '❌ '}
                              {fatura.status_pagamento === 'Pendente' && '⏳ '}
                              {fatura.status_pagamento === 'Excluída' && '🗑️ '}
                              {fatura.status_pagamento}
                            </span>
                          </td>
                          <td className={parseInt(fatura.dias_atraso) > 0 ? 'td-atraso' : ''}>
                            {parseInt(fatura.dias_atraso) > 0 ? `${fatura.dias_atraso} dias` : '-'}
                          </td>
                          <td>
                            {fatura.liquidado === 'S' ? (
                              <div className="td-pagamento">
                                <div>{formatMoeda(fatura.vlr_liquidacao)}</div>
                                <div className="td-data-pag">{formatData(fatura.data_liquidacao)}</div>
                              </div>
                            ) : '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Paginação */}
              {pagination.totalPages > 1 && (
                <div className="pagination-info">
                  Mostrando {faturas.length} de {pagination.total} faturas - 
                  Página {pagination.page} de {pagination.totalPages}
                </div>
              )}
            </>
          ) : (
            <div className="no-data">
              <div className="no-data-icon">📊</div>
              <p>Nenhuma fatura encontrada para {periodo?.mesNome}/{periodo?.ano}</p>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default Financeiro;

